import { Button, Text } from '@react-spectrum/s2';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import { useCallback, useState } from 'react';

import { TypewriterOnce } from '../components/TypewriterOnce';

type JobPreviewScreenProps = {
	onStart: () => void;
};

const SOURCES: Array<{ logo: string; label: string; desc: string }> = [
	{
		logo: '/logo-reddit.svg',
		label: 'Reddit',
		desc: 'Subreddits where your customers talk about products, problems, and recommendations.',
	},
	{
		logo: '/logo-instagram.svg',
		label: 'Instagram',
		desc: "What similar businesses post, what's getting traction, and which angles are landing.",
	},
	{
		logo: '/logo-google.svg',
		label: 'Google',
		desc: "What people search for in your space, who's ranking, and what content is winning the click.",
	},
	{
		logo: '/logo-reviews.svg',
		label: 'Review Sites',
		desc: 'Yelp, Google Reviews, Trustpilot — what real customers say about similar businesses and the category.',
	},
];

export const JobPreviewScreen = ({ onStart }: JobPreviewScreenProps) => {
	const [typingDone, setTypingDone] = useState(false);
	const handleTypingDone = useCallback(() => setTypingDone(true), []);

	return (
		<div className="flex h-full w-full items-center justify-center">
			<div className="w-full max-w-[760px] px-8">
				<h2
					className="mb-8"
					style={{
						fontSize: 'clamp(26px, 4vw, 40px)',
						fontWeight: 700,
						letterSpacing: '-0.02em',
						lineHeight: 1.15,
						color: 'var(--text-primary)',
					}}
				>
					<TypewriterOnce
						text="Here's what I'd watch. Click Start Job when you're ready."
						onDone={handleTypingDone}
					/>
				</h2>

				<div
					style={{
						opacity: typingDone ? 1 : 0,
						transition: 'opacity 400ms ease',
						pointerEvents: typingDone ? 'auto' : 'none',
					}}
				>
					<div
						className="mb-8"
						style={{
							background: 'white',
							border: '1px solid #E5E7EB',
							borderRadius: 16,
							padding: 24,
						}}
					>
						<div
							style={{
								fontSize: 11,
								fontWeight: 700,
								letterSpacing: '0.08em',
								color: '#6B7280',
								marginBottom: 8,
							}}
						>
							JOB
						</div>
						<div
							style={{
								fontSize: 20,
								fontWeight: 700,
								letterSpacing: '-0.01em',
								color: 'var(--text-primary)',
								marginBottom: 6,
							}}
						>
							Discover & monitor similar businesses
						</div>
						<p
							style={{
								fontSize: 14,
								lineHeight: 1.45,
								color: '#4B5563',
								marginBottom: 20,
							}}
						>
							Finds similar businesses in your space and watches what&apos;s
							working for them — angles, posts, and ideas you can borrow.
						</p>

						<div
							style={{
								fontSize: 11,
								fontWeight: 700,
								letterSpacing: '0.08em',
								color: '#6B7280',
								marginBottom: 10,
							}}
						>
							WHERE I&apos;LL LOOK
						</div>

						<div className="flex flex-col" style={{ gap: 10 }}>
							{SOURCES.map((s) => (
								<div
									key={s.label}
									className="flex items-start"
									style={{ gap: 10 }}
								>
									<div
										className="flex shrink-0 items-center justify-center"
										style={{
											width: 28,
											height: 28,
											borderRadius: 8,
											background: '#F4F4F5',
											padding: 4,
										}}
									>
										<img
											src={s.logo}
											alt=""
											style={{
												width: '100%',
												height: '100%',
												display: 'block',
												objectFit: 'contain',
											}}
										/>
									</div>
									<div style={{ flex: 1, paddingTop: 1 }}>
										<span
											style={{
												fontSize: 13,
												fontWeight: 600,
												color: 'var(--text-primary)',
											}}
										>
											{s.label}
										</span>
										<span
											style={{
												fontSize: 13,
												lineHeight: 1.35,
												color: '#4B5563',
											}}
										>
											{' — '}
											{s.desc}
										</span>
									</div>
								</div>
							))}
						</div>

						<p
							style={{
								fontSize: 12,
								color: '#6B7280',
								marginTop: 14,
							}}
						>
							+ TikTok, YouTube, forums, and more as I learn what&apos;s working
							for you.
						</p>
					</div>

					<Button
						variant="primary"
						staticColor="black"
						fillStyle="fill"
						size="L"
						onPress={onStart}
					>
						<Text>Start Job</Text>
						<ChevronRight />
					</Button>
				</div>
			</div>
		</div>
	);
};
